import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import {
  TEntityActions,
  TIMELINE_MIN_DAYS_TO_SHOW,
  TRectDropElement,
  TTimelineConfiguration,
  TTimelineIndicators,
  TTimelineModel,
  TToolbarAction,
} from './model/timeline.types';
import { TTimelineChartGroups, createTimelineChart } from './model/d3/draw';
import { addMarkers, addStripEvent, addStripOnCursor, removeCursor } from './model/d3/events/events';
import { addStripDrag, removeStripDrag, TStripDragHandle } from './model/d3/events/drag.tasks';
import { DRAW_OVERLAYS } from './model/d3/draw/overlay.tasks';
import { TIMELINE_D3, TDrawModel } from './model/draw.model';
import { DRAW_MODEL_UTILS } from './model/d3/utils';
import { TUserPaginationOption } from '../listing-pagination/pagination.types';
import { TPaginationInfo, TLL_RECALIBRATE_TYPES, TTimelineDateRangeDTO, TDate } from '@monorepo/types';
import { MenuController, ModalController, PopoverController } from '@ionic/angular';
import {
  getAvailableDateRangeInTimeline,
  timelineResetZoomDateRange,
  timelineZoomDateRange,
} from './model/timeline.utils';
import { DateUtils } from '@monorepo/utils';
import { KEYBOARD_UTILS } from '@monorepo/utils';

export type TToolbarActionEventEmit = [Exclude<TToolbarAction, 'SEARCH'>, Event] | ['SEARCH', Event | null];

@Component({
  selector: 'monorepo-timeline',
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss'],
})
export class TimelineComponent implements AfterViewInit, OnDestroy {
  #chartGroup: TTimelineChartGroups | null = null;
  #viewDateRange: TTimelineDateRangeDTO | null = null;
  #drawModel: TDrawModel | null = null;
  #scrollRangeListener: (() => void) | null = null;
  #zoomWheelListener: ((event: WheelEvent) => void) | null = null;
  #zoomRafId: number | null = null;
  #timelineWrapperScrollEl: HTMLDivElement | null = null;
  #programmaticScrollTimer: ReturnType<typeof setTimeout> | null = null;
  #prevScrollLeft = 0;
  #isDragging = false;
  #stripDrag: TStripDragHandle = null;
  zoomState: 'MAX' | 'MIN' | 'MID' | null = null;

  timelineView: TTimelineModel['boardView']['boardViewType'] = 'WEEKLY';
  hideLeftPanel?: boolean = undefined;

  // Setting Timeline model
  _timelineModel!: TTimelineModel;
  @Input() set timelineModel(value: TTimelineModel) {
    this._timelineModel = value;
    this.timelineView = value.boardView.boardViewType;
    this.hideLeftPanel = value.hideLeftPanel;

    // new dates can invalidate in-flight gesture
    // so abort it rather than let a drop land on stale positions
    this.#stripDrag?.cancel();
    this.#renderTimeline(this._timelineModel, true);
  }

  // Pagination inputs
  @Input() paginationOption?: TUserPaginationOption;
  @Input() paginationInfo?: TPaginationInfo;

  // Toolbard options
  @Input() toolbarActions: TToolbarAction[] = [];

  // Render Type to change configuration
  @Input() configuration: TTimelineConfiguration = {
    renderType: 'TNA_BOARD', // Default
  };

  @Input() isFilterActive = false;

  //Only disabling strip Left click and right click events [PLN-160]
  @Input() disableStripEvents = false;

  @Input() hideOpenInfoBtn = false;

  #disableStripDrag = false;
  @Input() set disableStripDrag(value: boolean) {
    this.#disableStripDrag = value;
    this.#updateStripDrag();
  }

  #validRowIds: string[] | null = null;
  @Input() set validRowIds(ids: string[] | null) {
    this.#validRowIds = ids;
    this.#drawDisabledOverlays();
  }

  #loadStrip: TRectDropElement[] | null = null;

  @Input() set loadStrip(value: TRectDropElement[] | null) {
    if (value) {
      this.#loadStrip = value;
      this.addStrip();
    } else {
      this.#loadStrip = null;
      this.removeStrip();
    }
    this.#updateStripDrag();
  }

  _markers: TTimelineIndicators = { indicators: [] };
  @Input() set markers(value: TTimelineIndicators) {
    this._markers = value;
    this.addMarkers();
  }

  @Input() set viewDateRange(range: TTimelineDateRangeDTO | null) {
    this.#updateViewDateRange(range);
  }

  @Input() maxDaysToShow: number | null = null;
  // ===================

  // Strip Events
  @Output() stripEvent = new EventEmitter<TEntityActions>();
  @Output() openFilter = new EventEmitter();
  @Output() recalibrate = new EventEmitter<TLL_RECALIBRATE_TYPES>();
  @Output() openInfo = new EventEmitter<Event>();
  @Output() updatedPaginationOption = new EventEmitter<TUserPaginationOption>();

  @Output() onToolbarAction = new EventEmitter<TToolbarActionEventEmit>();
  @Output() onTimelineUpdate = new EventEmitter<TTimelineModel['boardView']>();
  @Output() onTimelineView = new EventEmitter<Event>();
  @Output() onPrev = new EventEmitter<TTimelineModel['boardView']>();
  @Output() onNext = new EventEmitter<TTimelineModel['boardView']>();
  @Output() onToday = new EventEmitter<TTimelineModel['boardView']>();
  @Output() onScrollViewChange = new EventEmitter<TTimelineDateRangeDTO>();
  @Output() onZoom = new EventEmitter<TTimelineDateRangeDTO>();
  @Output() onDateClick = new EventEmitter<TDate>();

  @ViewChild('timeline') timelineElm!: ElementRef<HTMLDivElement>;

  #resizeObserver: ResizeObserver;
  constructor(
    private modalController: ModalController,
    private popoverController: PopoverController,
    private menuController: MenuController,
    private ngZone: NgZone,
  ) {
    this.#resizeObserver = new ResizeObserver((entries) => {
      entries.forEach(() => {
        this.ngZone.run(() => this.#renderTimeline(this._timelineModel));
      });
    });
  }

  // keyboard arrows
  @HostListener('window:keydown', ['$event'])
  async onKeyPress(event: KeyboardEvent) {
    const hostEl = this.timelineElm?.nativeElement;
    if (!hostEl || !hostEl.isConnected || hostEl.offsetParent === null || KEYBOARD_UTILS.isFromEditableElement(event))
      return;

    const isModalOpen = await this.modalController.getTop();
    const isPopoverOpen = await this.popoverController.getTop();
    const isMenuOpen = await this.menuController.isOpen();
    if (isModalOpen || isPopoverOpen || isMenuOpen) return;
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.onToolbarAction.emit(['SEARCH', null]);
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.onPrev.emit(this._timelineModel.boardView);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.onNext.emit(this._timelineModel.boardView);
    }
  }

  #updateViewDateRange(range: TTimelineDateRangeDTO | null) {
    const prevRange = this.#viewDateRange;
    this.#viewDateRange = range;

    const newDays = range ? DateUtils.dateRangeDiff(range, 'days') + 1 : null;
    if (newDays === null || this.maxDaysToShow === null) this.zoomState = null;
    else if (newDays <= TIMELINE_MIN_DAYS_TO_SHOW) this.zoomState = 'MIN';
    else if (newDays >= this.maxDaysToShow) this.zoomState = 'MAX';
    else this.zoomState = 'MID';

    if (!this.#drawModel) return;
    this.#stripDrag?.cancel();

    const prevDays = prevRange ? DateUtils.dateRangeDiff(prevRange, 'days') + 1 : null;

    if (prevDays !== newDays) this.#renderTimeline(this._timelineModel);
    else this.#scrollToViewRange();
  }

  removeStrip() {
    if (this.#chartGroup) removeCursor(this.#chartGroup);
  }

  addStrip() {
    if (this.#chartGroup && this.#loadStrip && this.#drawModel) {
      addStripOnCursor(this.#loadStrip, this.#chartGroup, this.#drawModel, (e) => {
        this.stripEvent.emit(e);
      });
    }
  }

  addMarkers() {
    if (this.#chartGroup && this.#drawModel) {
      addMarkers(this.#chartGroup, this.#drawModel, this._markers.indicators);
    }
  }

  #updateStripDrag() {
    if (!this.#chartGroup || !this.#drawModel) return;

    this.#stripDrag?.cancel();
    removeStripDrag(this.#chartGroup);
    this.#stripDrag = null;
    if (!this.configuration.enableStripDrag || this.#disableStripDrag || this.#loadStrip) return;

    this.#stripDrag = addStripDrag(this.#chartGroup, this.#drawModel, (e) => this.stripEvent.emit(e), {
      runOutsideAngular: (fn) => this.ngZone.runOutsideAngular(fn),
      run: (fn) => this.ngZone.run(fn),
      getScrollEl: () => this.#timelineWrapperScrollEl,
      scrollBy: (deltaX, deltaY) => {
        const scrollEl = this.#timelineWrapperScrollEl;
        if (!scrollEl) return;
        if (deltaX !== 0) this.#scrollLeftTo(scrollEl.scrollLeft + deltaX);
        if (deltaY !== 0) scrollEl.scrollTop += deltaY;
      },
      onDragStateChange: (isDragging) => this.#setDragging(isDragging),
    });
  }

  #drawDisabledOverlays() {
    if (!this.#chartGroup || !this.#drawModel) return;
    DRAW_OVERLAYS.drawDisabledEntityOverlays(
      this.#chartGroup.disabledOverlayTargets,
      this.#drawModel,
      this.#validRowIds,
    );
  }

  #setDragging(isDragging: boolean) {
    this.#isDragging = isDragging;
    if (isDragging) return;

    this.ngZone.run(() => this.#emitScrollViewChange());
  }

  changeTimelineView($event: Event) {
    const event = $event as unknown as { detail: { value: 'DAILY' | 'WEEKLY' | 'MONTHLY' } };
    const value = event.detail.value;
    this.onTimelineUpdate.emit({
      ...this._timelineModel.boardView,
      boardViewType: value,
    });
  }

  #renderTimeline(timelineModel: TTimelineModel, scrollHighlightedStripIntoView = false) {
    // Create Draw Model only if the timeline elm is defined
    if (this.timelineElm && this.timelineElm.nativeElement) {
      const drawModel = TIMELINE_D3.createDrawModel(
        this.timelineElm,
        timelineModel,
        this.configuration,
        this.#viewDateRange ?? undefined,
      );
      this.#drawModel = drawModel;
      this.#chartGroup = createTimelineChart(
        drawModel,
        (e) => this.stripEvent.emit(e),
        this.configuration.enableDateClick ? (date) => this.onDateClick.emit(date as TDate) : undefined,
        this.#validRowIds,
      );
      if (!this.#timelineWrapperScrollEl) {
        this.#timelineWrapperScrollEl = this.timelineElm.nativeElement.querySelector(
          '.timeline-wrapper',
        ) as HTMLDivElement;
      }

      addStripEvent(this.#chartGroup, drawModel, (timelineEventType) => {
        if (!this.disableStripEvents) {
          this.stripEvent.emit(timelineEventType);
        }
      });
      this.addStrip();
      this.addMarkers();
      this.#updateStripDrag();
      if (this.configuration.enableZoom) this.#setupZoomTracking();
      if (this.configuration.enableHorizontalScroll) this.#setupScrollRangeTracking();
      this.#scrollToViewRange();
      if (scrollHighlightedStripIntoView) this.#scrollFirstHighlightedStripIntoView();
    }
  }

  #scrollLeftTo(position: number) {
    if (!this.#timelineWrapperScrollEl) return;

    if (this.#programmaticScrollTimer) clearTimeout(this.#programmaticScrollTimer);
    this.#programmaticScrollTimer = setTimeout(() => (this.#programmaticScrollTimer = null), 50);

    this.#timelineWrapperScrollEl.scrollLeft = position;
    this.#prevScrollLeft = this.#timelineWrapperScrollEl.scrollLeft;
  }

  #scrollToViewRange() {
    if (!this.#viewDateRange || !this.#drawModel) return;

    requestAnimationFrame(() => {
      if (!this.#drawModel || !this.#viewDateRange) return;
      this.#scrollLeftTo(DRAW_MODEL_UTILS.getPostionByDate(this.#viewDateRange.from as TDate, this.#drawModel));
    });
  }

  #scrollFirstHighlightedStripIntoView() {
    requestAnimationFrame(() => {
      // Step 1: Scroll horizontally to the date range
      if (this.#viewDateRange && this.#drawModel) {
        this.#scrollLeftTo(DRAW_MODEL_UTILS.getPostionByDate(this.#viewDateRange.from as TDate, this.#drawModel));
      }

      // Step 2: Scroll vertically to the highlighted strip
      const firstHighlightedStrip = this._timelineModel?.entities
        .flatMap((g) => g.entities)
        .flatMap((e) => e.elements)
        .find((el) => el.type === 'STRIP' && el.isStripHighlighted);
      if (firstHighlightedStrip) {
        const stripElement = document.getElementById(firstHighlightedStrip.id);
        if (stripElement) {
          stripElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    });
  }

  #setupScrollRangeTracking() {
    if (!this.#timelineWrapperScrollEl) return;

    if (this.#scrollRangeListener) {
      this.#timelineWrapperScrollEl.removeEventListener('scroll', this.#scrollRangeListener);
      this.#scrollRangeListener = null;
    }

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    this.#prevScrollLeft = this.#timelineWrapperScrollEl.scrollLeft;

    this.#scrollRangeListener = () => {
      if (this.#programmaticScrollTimer || this.#isDragging) return;
      if (debounceTimer) clearTimeout(debounceTimer);

      debounceTimer = setTimeout(() => {
        if (!this.#timelineWrapperScrollEl) return;
        if (this.#timelineWrapperScrollEl.scrollLeft === this.#prevScrollLeft) return; // vertical scroll only - skip
        this.#emitScrollViewChange();
      }, 150);
    };

    this.#timelineWrapperScrollEl.addEventListener('scroll', this.#scrollRangeListener);
  }

  #emitScrollViewChange() {
    if (!this.configuration.enableHorizontalScroll) return;
    if (!this.#drawModel || !this.#timelineWrapperScrollEl || !this.#viewDateRange) return;

    const viewDays = DateUtils.dateRangeDiff(this.#viewDateRange, 'days') + 1;
    const availableDateRange = getAvailableDateRangeInTimeline(this.#drawModel.timelineModel);

    const scrollLeft = this.#timelineWrapperScrollEl.scrollLeft;
    this.#prevScrollLeft = scrollLeft;

    const from = DRAW_MODEL_UTILS.getDateByXPosition(scrollLeft, this.#drawModel);
    const to = DateUtils.addToDate(from, 'days', viewDays - 1);

    const range = DateUtils.fitDateRange({ from, to }, availableDateRange);

    if (range.from === this.#viewDateRange.from && range.to === this.#viewDateRange.to) return;
    this.onScrollViewChange.emit(range);
  }

  #setupZoomTracking() {
    if (!this.#timelineWrapperScrollEl) return;

    if (this.#zoomWheelListener) {
      this.#timelineWrapperScrollEl.removeEventListener('wheel', this.#zoomWheelListener);
      this.#zoomWheelListener = null;
    }

    this.#zoomWheelListener = (event: WheelEvent) => {
      if (!event.ctrlKey) return;
      if (this.#isDragging) return; // zoom rebuilds the draw model and would break the gesture
      event.preventDefault(); // don't perform browser's zoom actions

      if (this.#zoomRafId === null) {
        this.#zoomRafId = requestAnimationFrame(() => {
          this.#applyZoom(event.deltaY, event.clientX);
          this.#zoomRafId = null;
        });
      }
    };

    this.#timelineWrapperScrollEl.addEventListener('wheel', this.#zoomWheelListener, { passive: false });
  }

  #applyZoom(totalDelta: number, clientX: number) {
    if (!this.#drawModel || !this.#timelineWrapperScrollEl || this.maxDaysToShow === null) return;

    // threshold: how much trackpad delta = 1 day zoom
    // larger value => less sensitive
    const ZOOM_DELTA_PER_DAY = 5;
    const unit = Math.floor(Math.abs(totalDelta) / ZOOM_DELTA_PER_DAY);
    const zoomStep = Math.min(unit, 2); // max 2 days of zoom at once
    if (zoomStep === 0) return;

    const availableDateRange = getAvailableDateRangeInTimeline(this.#drawModel.timelineModel);
    const viewDateRange = this.#viewDateRange ?? availableDateRange;

    const maxDays = this.maxDaysToShow;

    // determine the date under the cursor to use as zoom anchor
    const rect = this.#timelineWrapperScrollEl.getBoundingClientRect();
    const cursorXInViewport = clientX - rect.left - this.#drawModel.layout.margin.left;
    const scrollLeft = this.#timelineWrapperScrollEl.scrollLeft;
    const anchorDate = DRAW_MODEL_UTILS.getDateByXPosition(
      Math.max(0, scrollLeft + cursorXInViewport),
      this.#drawModel,
    );

    const newRange: TTimelineDateRangeDTO = timelineZoomDateRange(
      viewDateRange,
      zoomStep * (totalDelta > 0 ? 1 : -1),
      availableDateRange,
      maxDays,
      TIMELINE_MIN_DAYS_TO_SHOW,
      anchorDate,
    );
    this.#viewDateRange = newRange;
    this.#renderTimeline(this._timelineModel);
    this.onZoom.emit(newRange);
  }

  ngAfterViewInit(): void {
    this.#renderTimeline(this._timelineModel);
    this.#resizeObserver.observe(this.timelineElm.nativeElement);
  }

  ngOnDestroy(): void {
    this.#stripDrag?.cancel();
    if (this.#chartGroup) removeStripDrag(this.#chartGroup);
    this.#stripDrag = null;
    this.#resizeObserver.disconnect();
    if (this.#timelineWrapperScrollEl && this.#scrollRangeListener) {
      this.#timelineWrapperScrollEl.removeEventListener('scroll', this.#scrollRangeListener);
    }
    if (this.#timelineWrapperScrollEl && this.#zoomWheelListener) {
      this.#timelineWrapperScrollEl.removeEventListener('wheel', this.#zoomWheelListener);
    }
    if (this.#zoomRafId !== null) cancelAnimationFrame(this.#zoomRafId);
    if (this.#programmaticScrollTimer) {
      clearTimeout(this.#programmaticScrollTimer);
      this.#programmaticScrollTimer = null;
    }
  }

  updateUserPagination($event: TUserPaginationOption) {
    this.paginationOption = $event;
    this.updatedPaginationOption.emit($event);
  }

  resetZoom() {
    if (!this.#drawModel || !this.#viewDateRange || this.maxDaysToShow === null) return;
    const availableDateRange = getAvailableDateRangeInTimeline(this.#drawModel.timelineModel);
    const viewDateRange = this.#viewDateRange;
    const newRange = timelineResetZoomDateRange(viewDateRange, availableDateRange, this.maxDaysToShow);
    this.#updateViewDateRange(newRange);
    this.onZoom.emit(newRange);
  }

  zoomIn() {
    if (!this.#viewDateRange || this.maxDaysToShow === null) return;
    const newRange = timelineZoomDateRange(
      this.#viewDateRange,
      -2,
      this.#viewDateRange,
      this.maxDaysToShow,
      TIMELINE_MIN_DAYS_TO_SHOW,
    );
    if (!newRange) return;
    this.#updateViewDateRange(newRange);
    this.onZoom.emit(newRange);
  }

  zoomOut() {
    if (!this.#viewDateRange || !this.#drawModel || this.maxDaysToShow === null) return;

    const newRange = timelineZoomDateRange(
      this.#viewDateRange,
      2,
      getAvailableDateRangeInTimeline(this.#drawModel.timelineModel),
      this.maxDaysToShow,
    );
    if (!newRange) return;
    this.#updateViewDateRange(newRange);
    this.onZoom.emit(newRange);
  }

  print() {
    const mywindow = window.open('', 'PRINT', 'height=400,width=600');

    if (mywindow) {
      mywindow.document.write('<html><head><title>' + document.title + '</title>');
      mywindow.document.write(`<style>
      body {
        font-family: Roboto, "Helvetica Neue", sans-serif;
        padding:0;
        margin:0;
      }
      .header, .left-nav {
        position: absolute !important;
      }
      @media print{@page {size: landscape}}
      .timeline-wrapper {
        overflow: visible !important;
      }
      </style></head><body>`);
      mywindow.document.write(this.timelineElm.nativeElement.innerHTML);
      mywindow.document.write('</body></html>');

      mywindow.print();

      return true;
    }
    return false;
  }
}
